import { supabase } from "@/integrations/supabase/client";

// All supported rate limit action types
type RateLimitAction = 
  | 'signup' 
  | 'login' 
  | 'beta_signup' 
  | 'beta_application'
  | 'beta_contact'
  | 'support_message' 
  | 'beta_feedback';

interface RateLimitCheck {
  action: RateLimitAction;
  identifier: string;
}

interface RateLimitResult {
  allowed: boolean;
  error?: string;
  remaining?: number;
  escalationLevel?: number;
  retryAfter?: number;
}

/**
 * Check rate limit using the enhanced rate limit edge function.
 * Provides per-identifier tracking with escalating lockouts for abuse prevention.
 */
export const checkRateLimit = async ({ action, identifier }: RateLimitCheck): Promise<RateLimitResult> => {
  // E2E/dev bypass: avoid flakey local Playwright runs tripping lockouts.
  // Only bypass on localhost AND only for test-style identifiers.
  try {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    const id = (identifier || '').toLowerCase();
    const isTestIdentifier = id.includes('e2e') || id.includes('@test.') || id.endsWith('@example.com');

    if (isLocal && isTestIdentifier) {
      return { allowed: true };
    }
  } catch {
    // ignore
  }

  try {
    // In installed PWAs / flaky networks, function invocations can sometimes hang.
    // We fail-open after a short timeout to avoid blocking auth flows.
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 3000);

    const { data, error } = await (supabase.functions as any).invoke(
      'enhanced-rate-limit-check',
      {
        body: { action, identifier },
        signal: controller.signal,
      }
    );

    window.clearTimeout(timeoutId);

    if (error) {
      // If rate limit service fails, allow the request but log the error
      console.error('Rate limit check failed:', error);
      return { allowed: true };
    }

    return {
      allowed: data.allowed,
      error: data.error,
      remaining: data.remaining,
      escalationLevel: data.escalationLevel,
      retryAfter: data.retryAfter
    };
  } catch (error) {
    // Fail open - if rate limiting is down, don't block users
    console.error('Rate limit check error:', error);
    return { allowed: true };
  }
};

/**
 * Legacy wrapper that calls the basic rate-limit-check function.
 * @deprecated Use checkRateLimit instead which uses enhanced rate limiting
 */
export const checkRateLimitBasic = async ({ action, identifier }: RateLimitCheck): Promise<RateLimitResult> => {
  try {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 3000);

    const { data, error } = await (supabase.functions as any).invoke('rate-limit-check', {
      body: { action, identifier },
      signal: controller.signal,
    });

    window.clearTimeout(timeoutId);

    if (error) {
      console.error('Rate limit check failed:', error);
      return { allowed: true };
    }

    return {
      allowed: data.allowed,
      error: data.error,
      remaining: data.remaining
    };
  } catch (error) {
    console.error('Rate limit check error:', error);
    return { allowed: true };
  }
};
